<template>
  <swiper class="swiper-no-swiping">
    <swiper-slide>
      <b-container fluid class="steps flex-fill">
        <b-card class="step" no-body>
          <div slot="header">
            <span>My Saved Homes</span>
            &nbsp;
            <a v-b-popover="popoverConfig" class="info-button">
              <i class="fa fa-info-circle" />
            </a>
          </div>
          <div class="step-cards">
            <div class="p-2 w-100 toggle-ai">
              <b-btn
                block
                :variant="!isAIMasking ? 'primary' : undefined"
                @click="setAIMasking(!isAIMasking)"
                class="toggle-ai"
              >
                {{ isAIMasking ? 'Edit door openings on my own' :
                  'Detect door openings automatically'}}
              </b-btn>
            </div>
            <image-uploader
              v-if="!isAIMasking"
              @upload="handleProjectUpload"
              :label="'Upload a photo to edit openings on your own'"
            />
            <image-uploader
              v-if="isAIMasking"
              @upload="handleProjectUpload"
              :useAI="true"
              :label="'Upload a photo to see your door(s) in place'"
            />
            <project-card
              v-for="project of uploadedProjects"
              :key="project.folder"
              :project="project"
              @picked="handleUploadedPicked(project)"
              @remove="handleProjectRemove(project)"
            />
          </div>
        </b-card>
        <b-card v-if="!isHub" class="step" no-body header="Sample Homes">
          <div class="step-cards">
            <project-card
              v-for="project of sampleProjects"
              :key="project.folder"
              :project="project"
              @picked="handleSamplePicked(project)"
            />
          </div>
        </b-card>
      </b-container>
      <b-container v-if="!isHub" fluid class="flex-none py-3">
        <b-row>
          <b-col>
            <!---->
          </b-col>
          <b-col class="text-right">
            <b-btn
              variant="primary"
              @click.prevent="setCurrentSlide('configurator/products')"
            >
              Place Door
            </b-btn>
          </b-col>
        </b-row>
      </b-container>
    </swiper-slide>
    <swiper-slide id="project">
      <b-container fluid class="steps flex-fill">
        <b-card class="step" no-body>
          <b-card-header>Add a new garage door</b-card-header>
          <b-card-body>
            <door-dimensioner
              ref="doorDimensionsAdd"
              show-type-controls
              reset-on-save
              @save="(layer, properties) => handleLayerCreate(currentLayer, properties)"
            />
          </b-card-body>
        </b-card>

        <b-card v-if="currentLayer" class="step" no-body>
          <b-card-header>Make changes to the selected door</b-card-header>
          <b-card-body>
            <door-dimensioner
              :layer="currentLayer"
              show-edit-controls
              @duplicate="handleLayerCreate"
              @save="handleLayerUpdate"
              @remove="handleLayerRemove"
            >
              <p slot="dimensions-label">
                Make changes to <span class="text-primary">{{ currentLayer.name }}</span>
              </p>
              <div class="pt-3">
                <p>To change the size of this door/opening, enter a new size and select UPDATE.</p>
                <p>To create a new door/opening of the same size, select DUPLICATE.</p>
                <p>To delete this door/opening, select REMOVE.</p>
              </div>
            </door-dimensioner>
          </b-card-body>
        </b-card>
      </b-container>
      <footer class="d-flex align-items-center justify-content-end flex-none p-3">
        <b-btn
          v-if="isHub"
          variant="secondary"
          @click.prevent="setCurrentSlide(['projects/list', 'configurator/projects'])"
          style="margin-right: 10px;"
        >
          CHANGE PHOTO
        </b-btn>
        <b-btn
          v-if="isHub"
          variant="primary"
          @click.prevent="handleEmbeddedClose"
        >
          Confirm
        </b-btn>
        <b-btn
          v-else
          variant="primary"
          @click.prevent="setCurrentSlide('configurator/products')"
          class="overflow-ellipsis"
        >
          Place Door
        </b-btn>
      </footer>
    </swiper-slide>
  </swiper>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import ImageUploader from '@/components/ImageUploader';
import DoorDimensioner from '@/components/DoorDimensioner';
import ProjectCard from '@/components/ProjectCard';
import SpinnerButton from '@/components/utility/SpinnerButton';
import {
  packDimensions,
  unpackDimensions,
  pointsChange,
  getDoorPointsFromShape,
} from '@/helpers/dimensions';
import saveRenderProject from '@/helpers/project';
import { sendPostMessage } from '@/helpers/embedded';


export default {
  name: 'projects',
  props: {
    rendering: Boolean,
  },
  data() {
    return {
      uploadedProjects: [],
      sampleProjects: [],

      popoverConfig: {
        trigger: 'hover',
        placement: 'top',
        content: `
For best results, image must be:

- JPG, JPEG or PNG formats
- No larger than 18 MB in file size
- Landscape format (wider than tall)
- High resolution and in focus
- An unobstructed view of the home
- Well centered on area of interest`,
      },
    };
  },
  computed: {
    ...mapState([
      'renderProject',
      'shellProject',
      'currentLayer',
      'currentSlides',
      'specialCases',
    ]),
    ...mapGetters([
      'isHub',
      'getSlideIndex',
      'currentProject',
      'isAIMasking',
      'hubDefaultWidth',
      'hubDefaultHeight',
    ]),
  },
  watch: {
    currentSlides: {
      handler() {
        // map to slideIndex
        const slideIndex = this.getSlideIndex('projects');

        // swipe to slide
        if (slideIndex >= 0) {
          this.$el.swiper.slideTo(slideIndex);
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations([
      'setRenderProject',
      'setCurrentLayer',
      'setCurrentView',
      'setCurrentSlide',
      'setAIMasking',
    ]),
    async pickProject(project, preLoaded = false) {
      if (!preLoaded) {
        // reload layers even if the project is loaded, as the mask might have changed on disk
        await project.load(); // @TODO: show loading spinner while this is happening
      }

      // mark it as current
      this.setRenderProject(project);

      if (project.layers.length > 0) {
        // Synchronize the shell project with the loaded project
        const shellLayersPre = this.shellProject.layers.length;
        const deltaLayers = project.layers.length - shellLayersPre;
        // Add or remove as many doors as needed to match the project
        if (deltaLayers > 0) {
          for (let i = 0; i < deltaLayers; i += 1) {
            this.handleLayerCreate(undefined, undefined, false); // don't select it
          }
        } else if (deltaLayers < 0) {
          for (let i = 0; i < Math.abs(deltaLayers); i += 1) {
            this.handleLayerRemove(this.shellProject.layers[shellLayersPre - 1 - i]);
          }
        }
        // Change dimensions where necessary
        this.shellProject.layers.forEach((l, index) => {
          const dimensionsRaw = unpackDimensions(project.layers[index]);
          const dimensions = packDimensions(dimensionsRaw);
          // perspective is used to calculate ppf on currentProject
          if (project.layers[index].perspective) {
            l.perspective = project.layers[index].perspective.clone();
          }
          this.handleLayerUpdate(l, dimensions);
        });
      } else {
        // Project has no layers, remove all doors (except for Door #1, which has all the steps)
        this.shellProject.layers.forEach((l, index) => {
          if (index > 0) {
            this.handleLayerRemove(l);
          }
        });
      }

      // switch to project view
      this.setCurrentView(null);
    },
    async pickAIProject(project) {
      // mark it as current
      this.setRenderProject(project);

      if (project.layers.length > 0) {
        // Synchronize the shell project with the loaded project
        const shellLayersPre = this.shellProject.layers.length;
        const deltaLayers = project.layers.length - shellLayersPre;
        // Add or remove as many doors as needed to match the project
        if (deltaLayers > 0) {
          for (let i = 0; i < deltaLayers; i += 1) {
            this.handleLayerCreate(undefined, undefined, false); // don't select it
          }
        } else if (deltaLayers < 0) {
          for (let i = 0; i < Math.abs(deltaLayers); i += 1) {
            this.handleLayerRemove(this.shellProject.layers[shellLayersPre - 1 - i]);
          }
        }
        // Change dimensions where necessary
        this.shellProject.layers.forEach((l, index) => {
          const dimensionsRaw = unpackDimensions(project.layers[index]);
          const dimensions = packDimensions(dimensionsRaw);
          // perspective is used to calculate ppf on currentProject
          if (project.layers[index].perspective) {
            l.perspective = project.layers[index].perspective.clone();
          }
          this.handleLayerUpdate(l, dimensions);
        });
      } else {
        // Project has no layers, remove all doors (except for Door #1, which has all the steps)
        this.shellProject.layers.forEach((l, index) => {
          if (index > 0) {
            this.handleLayerRemove(l);
          }
        });
      }

      // switch to project view
      this.setCurrentView(null);
    },
    async handleSamplePicked(project) {
      await this.pickProject(project);
    },
    async handleAIUploadedPicked(project) {
      await this.pickAIProject(project);

      if (this.isHub) {
        // go to proejct mask editing screen
        this.setCurrentSlide('projects/item');
      } else {
        // go to product screen
        this.setCurrentSlide('configurator/products');
      }
    },
    async handleUploadedPicked(project, preLoaded = false) {
      await this.pickProject(project, preLoaded);

      // go to project mask editing screen
      this.setCurrentSlide('projects/item');
    },
    async handleProjectUpload({ uuid, useAI }) {
      // handle project creation
      const project = await window.Renoworks.ProjectController.createProjectFromUpload(uuid);
      // if project creation is rejected, an error string will be returned
      // duplicates are sometimes created, check for duplicate error string and return if so
      if (!project.folder) {
        if (project.indexOf('Duplicate') > -1) {
          return;
        }
        throw new Error(project);
      }

      // Need to check if AI project has layers
      let hasLayers = false;
      if (useAI) {
        hasLayers = await project.hasLayers();
        if (!hasLayers) {
          this.$root.alert(
            'No door detected. Please edit openings manually.',
            'Error',
          );
        }
      }
      const autoMasked = hasLayers;

      if (autoMasked) {
        await project.load();
        project.masked = true;
        if (this.hubDefaultWidth && this.hubDefaultHeight) {
          // If defaultWidth/Height defined by hub, use it
          // Width and Height are defined in inches
          const physicalWidth = this.hubDefaultWidth / 12;
          const physicalHeight = this.hubDefaultHeight / 12;
          project.layers.forEach((l) => {
            l.physicalWidth = physicalWidth;
            l.physicalHeight = physicalHeight;
          });
        } else if (this.currentLayer.physicalWidth && this.currentLayer.physicalHeight) {
          // Otherwise, use the size set by user
          project.layers.forEach((l) => {
            l.physicalWidth = this.currentLayer.physicalWidth;
            l.physicalHeight = this.currentLayer.physicalHeight;
          });
        }
      } else {
        const layer = (
          (
            this.renderProject &&
            this.renderProject.layers &&
            this.renderProject.layers[0]
          ) ||
          this.currentLayer
        );
        // set layer perspective and ppf based on wid and hgt of project
        layer.perspective = new window.Renoworks.Perspective();
        layer.perspective.initWithProjectDimensions(
          project.width,
          project.height,
          layer.physicalHeight ? layer.physicalWidth / layer.physicalHeight : undefined,
        );

        // uploaded projects should show no shadows
        layer.shadows = 2;
        this.currentLayer.shadows = 2;
        if (this.renderProject && this.renderProject.layers) {
          this.renderProject.layers.forEach((l) => {
            l.shadows = 2;
          });
        }

        // set project ppf based on new layer size
        // with multiple layers, the last one updated wins!
        if (this.currentLayer.physicalWidth && this.currentLayer.physicalHeight) {
          const averagePpf = window.Renoworks.Util.calculatePpf(
            layer.perspective.points,
            this.currentLayer.physicalWidth,
            this.currentLayer.physicalHeight,
          );
          // set current ppf for rendering the project(s)
          project.setPpf(Math.round(averagePpf));
        }
        project.layers.push(layer);
        window.Renoworks.ProjectController.setCurrentProject(project);

        // Save initialized project to disk incase of reload
        await project.save();
      }
      // set it as the render project
      this.setRenderProject(project);

      if (window.Renoworks.User.loggedIn) {
        // persist via user account
        // NB: this refreshes `uploadedProjects` via an event, so we do not update it here
        await window.Renoworks.User.saveUpload(project.folder);
      } else {
        // add to local collection, but only if unique
        this.uploadedProjects.push(project);
      }

      // update ui
      if (autoMasked) {
        await this.handleAIUploadedPicked(project);
      } else {
        await this.handleUploadedPicked(project, true);
      }
    },
    async handleProjectRemove(project) {
      // clear renderProject, if necessary
      if (project && this.renderProject && project.folder === this.renderProject.folder) {
        this.setRenderProject(null);
      }

      if (window.Renoworks.User.loggedIn) {
        // unlink from user account
        // NB: this refreshes `uploadedProjects` via an event, so no need to update it manually
        await window.Renoworks.User.deleteUpload(project.id);
      } else {
        // remove from local collection
        const index = this.uploadedProjects.indexOf(project);
        if (index >= 0) {
          this.uploadedProjects.splice(index, 1);
        }
      }
    },
    handleLayerCreate(layerToClone = undefined, properties = undefined, select = true) {
      const project = this.shellProject;
      if (project) {
        const projectWidth = this.renderProject.width;
        const projectHeight = this.renderProject.height;

        // initialize a new layer
        const newLayer = new window.Renoworks.Layer('Door');

        // uploaded projects should show no shadows
        newLayer.shadows = 2;

        // auto-increment integer id
        const nextLayerID = project.layers
          .reduce((max, otherLayer) => Math.max(parseInt(otherLayer.id, 10), max), 0) + 1;
        newLayer.id = nextLayerID;
        newLayer.name = `Door ${nextLayerID}`;

        // add special properties
        newLayer.product = layerToClone && layerToClone.product && layerToClone.product.clone();
        // N.B. 'steps' will be (re)initialized automatically later, so no need to clone it
        newLayer.currentStepIndex = layerToClone ? layerToClone.currentStepIndex : 0;
        newLayer.farthestStepIndex = layerToClone ? layerToClone.farthestStepIndex : 0;

        // port over perspective, but centered
        if (layerToClone && layerToClone.perspective) {
          newLayer.perspective = layerToClone.perspective.clone();
          newLayer.perspective.centerWithin(projectWidth, projectHeight);
        }

        // port over any explicit props
        if (properties) {
          Object.assign(newLayer, properties);

          // draw layer:
          //   - with physical-dimension-driven aspect ratio
          //   - centered on canvas
          //   - max-height constained (width can overflow)
          const { physicalWidth, physicalHeight } = properties;
          if (physicalWidth && physicalHeight) {
            newLayer.perspective = new window.Renoworks.Perspective();
            newLayer.perspective.initWithProjectDimensions(
              projectWidth,
              projectHeight,
              physicalWidth / physicalHeight,
              getDoorPointsFromShape(properties.doorShape),
            );
          }
        }

        // append to project
        project.layers.push(newLayer);
        // only add a clone layer to renderProject if it doesn't already have one
        // assume all IDs are added linearly (they should be)
        if (this.renderProject.layers.length < nextLayerID) {
          this.renderProject.layers.push(newLayer.clone());
        }

        // select
        if (select) {
          this.setCurrentLayer(newLayer);
        }
        if (newLayer.physicalWidth && newLayer.physicalHeight) {
          this.$emit('dimensionChange', newLayer);
        }
      }
    },
    handleLayerUpdate(layer, properties = undefined) {
      const project = this.shellProject;
      if (project) {
        const index = project.layers.indexOf(layer);
        const renderProjectLayer = this.renderProject.layers[index];
        if (index >= 0) {
          // update existing, if necessary
          if (properties) {
            if (layer.doorShape && layer.doorShape !== properties.doorShape) {
              const pointsDelta = pointsChange(layer.doorShape, properties.doorShape);
              if (pointsDelta && !isNaN(pointsDelta) && pointsDelta !== 0) {
                // shell project layers above index 0 do not have perspective
                if (layer.perspective) {
                  layer.perspective.changePerspectivePoints(
                    getDoorPointsFromShape(properties.doorShape));
                }
                // update render project, which also has perspective above index 0
                if (renderProjectLayer && renderProjectLayer.perspective) {
                  renderProjectLayer.perspective.changePerspectivePoints(
                    getDoorPointsFromShape(properties.doorShape));
                }
              }
            }
            // layer.doorShape is set here along with all properties
            Object.entries(properties).forEach(([k, v]) => {
              layer[k] = v;
              // update render project too, but only when layer exists
              if (renderProjectLayer) {
                renderProjectLayer[k] = v;
              }
            });
          }
        }
        // update the layer pattern and trigger a rerender
        this.$emit('dimensionChange', layer);
      }
    },
    handleLayerRemove(layer) {
      const project = this.shellProject;
      if (project) {
        const index = project.layers.indexOf(layer);
        if (index >= 0) {
          // remove from project
          project.layers.splice(index, 1);
          // update render project too, but only when layer exists
          if (index <= this.renderProject.layers.length - 1) {
            this.renderProject.layers.splice(index, 1);
          }

          // select last layer instead
          const lastLayer = project.layers[project.layers.length - 1];
          this.setCurrentLayer(lastLayer || null);
        }
      }
    },

    async handleEmbeddedClose() {
      await saveRenderProject(this.renderProject);

      sendPostMessage({
        action: 'close',
        payload: {
          project: this.renderProject.folder,
        },
      });
    },
  },
  async created() {
    await window.Renoworks.ProjectController.load();
    this.sampleProjects = window.Renoworks.ProjectController.getProjects();

    // jQuery(window.Renoworks.Event).on() sets 'this' to the Event object, so use that
    const that = this;

    // save anonymous uploaded projects to user account
    // or clear uploaded projects on log out
    function syncUpload() {
      if (window.Renoworks.User.loggedIn) {
        const savedProjects = window.Renoworks.ProjectController.getUploads();
        // don't save duplicate projects
        const savePromises = that.uploadedProjects
          .filter(project => !savedProjects.find(p => p.folder === project.folder))
          .map(project => window.Renoworks.User.saveUpload(project.folder));
        return Promise.all(savePromises);
      }
      that.uploadedProjects = [];
      return Promise.resolve();
    }

    window.jQuery(window.Renoworks.Event).on(window.Renoworks.Event.USER_LOADED, async () => {
      if (window.Renoworks.User.loggedIn) {
        // sign-in or profile update
        await syncUpload();
      } else {
        // sign-out
        // eslint-disable-next-line no-lonely-if
        if (that.renderProject && that.renderProject.tag === 'uploaded') {
          // clear current project if it was a user project
          that.setRenderProject(null);
        }
      }
    });

    // keep user uploadedProjects list synced with CF
    window.jQuery(window.Renoworks.Event).on(window.Renoworks.Event.UPLOADS_UPDATED, async () => {
      await syncUpload();
      that.uploadedProjects = window.Renoworks.ProjectController.getUploads();
    });

    if (this.$route.query.project) {
      const project = new window.Renoworks.Project();
      project.tag = 'uploaded';
      project.folder = this.$route.query.project;
      await this.handleUploadedPicked(project);
    }
  },
  components: {
    ImageUploader,
    DoorDimensioner,
    ProjectCard,
    SpinnerButton,
  },
};
</script>

<style lang="scss">
.projects {

}
</style>



// WEBPACK FOOTER //
// src/components/Projects.vue